import type { PaperWords } from '../ui/paperWords';
import { FONT_DISPLAY, FONT_TEXT } from './textures';

const INK = '#3b2a1e';
const RED = '#b8352b';
const PINK = '#f2d5c4';

function wrap(g: CanvasRenderingContext2D, text: string, width: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const t = line ? `${line} ${w}` : w;
    if (g.measureText(t).width > width && line) {
      lines.push(line);
      line = w;
    } else line = t;
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * The picture the paper's "share" sends (1200 × 630, a link preview's size): a clipping torn from your paper,
 * headlined about you, on the game's warm colours, with a red ticket inviting friends to race. JPEG, or null.
 */
export async function clippingPicture(w: PaperWords, photo: string, host: string): Promise<Blob | null> {
  const W = 1200;
  const H = 630;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d');
  if (!g) return null;
  await document.fonts?.load(`400 80px ${FONT_DISPLAY}`).catch(() => undefined);
  // The game behind, as if blurred: its sunset, the poster's cream in the middle.
  const bg = g.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#b0675a');
  bg.addColorStop(1, '#7a4a32');
  g.fillStyle = bg;
  g.fillRect(0, 0, W, H);
  const glow = g.createRadialGradient(W * 0.5, H * 0.5, 40, W * 0.5, H * 0.5, W * 0.55);
  glow.addColorStop(0, 'rgba(243,230,200,0.5)');
  glow.addColorStop(1, 'rgba(243,230,200,0)');
  g.fillStyle = glow;
  g.fillRect(0, 0, W, H);
  // The clipping: torn edges, a little turned.
  const img = photo && w.champion ? await new Promise<HTMLImageElement | null>((done) => {
    const i = new Image();
    i.onload = () => done(i);
    i.onerror = () => done(null);
    i.src = photo;
  }) : null;
  const cw = img ? 840 : 780;
  const ch = 420;
  g.save();
  g.translate(img ? 64 : 92, img ? 82 : 100);
  g.rotate((-3 * Math.PI) / 180);
  let seed = 3;
  const rnd = (): number => (seed = (seed * 16807) % 2147483647) / 2147483647;
  g.beginPath();
  g.moveTo(0, rnd() * 9);
  for (let x = 20; x <= cw; x += 20) g.lineTo(x, rnd() * 9);
  for (let y = 16; y <= ch; y += 16) g.lineTo(cw - rnd() * 7, y);
  for (let x = cw - 20; x >= 0; x -= 20) g.lineTo(x, ch - rnd() * 9);
  for (let y = ch - 16; y >= 0; y -= 16) g.lineTo(rnd() * 7, y);
  g.closePath();
  g.shadowColor = 'rgba(59,42,30,0.35)';
  g.shadowBlur = 24;
  g.shadowOffsetY = 12;
  g.fillStyle = PINK;
  g.fill();
  g.shadowColor = 'transparent';
  const pad = 32;
  g.fillStyle = INK;
  g.textBaseline = 'alphabetic';
  g.font = `400 26px ${FONT_DISPLAY}`;
  g.fillText('LA', pad, 62);
  g.font = `400 50px ${FONT_DISPLAY}`;
  g.fillText('GAZZETTA', pad + 32, 62);
  const gw = g.measureText('GAZZETTA').width;
  g.font = `400 26px ${FONT_DISPLAY}`;
  g.fillText('DEL GRAND PREMIO', pad + 42 + gw, 62);
  g.fillRect(pad, 74, cw - pad * 2, 3);
  g.fillRect(pad, 80, cw - pad * 2, 1);
  g.font = `700 13px ${FONT_TEXT}`;
  const date = w.dateline[1].replace(/^yesterday at /, '').toUpperCase();
  g.fillText(date.split('').join(' '), pad, 100);
  g.textAlign = 'right';
  g.fillText('YESTERDAY’S RACE', cw - pad, 100);
  g.textAlign = 'left';
  g.fillRect(pad, 108, cw - pad * 2, 1);
  const textW = img ? cw - pad * 2 - 300 - 22 : cw - pad * 2;
  g.font = `400 ${img ? 74 : 86}px ${FONT_DISPLAY}`;
  const lines = wrap(g, w.clipHead.toUpperCase(), textW).slice(0, 2);
  let y = 190;
  for (const l of lines) {
    g.fillText(l, pad, y);
    y += img ? 70 : 80;
  }
  g.font = `600 24px ${FONT_TEXT}`;
  for (const l of wrap(g, w.clipDeck, textW).slice(0, 2)) {
    g.fillText(l, pad, y - 22);
    y += 30;
  }
  if (img) {
    const pw = 300;
    const ph = (pw * img.height) / img.width;
    g.strokeStyle = 'rgba(59,42,30,0.6)';
    g.lineWidth = 1;
    g.drawImage(img, cw - pad - pw, 130, pw, ph);
    g.strokeRect(cw - pad - pw, 130, pw, ph);
  }
  // The stamps, in red, a little crooked.
  let sx = pad;
  // Under the words, however many lines they took.
  const sy = Math.min(ch - 40, Math.max(ch - 70, y + 14));
  g.font = `400 30px ${FONT_DISPLAY}`;
  (w.yours?.stamps ?? []).slice(0, 2).forEach((s, i) => {
    const t = s.toUpperCase();
    const tw = g.measureText(t).width + 22;
    g.save();
    g.translate(sx + tw / 2, sy);
    g.rotate(((i ? 2 : -4) * Math.PI) / 180);
    g.globalAlpha = 0.86;
    g.strokeStyle = RED;
    g.lineWidth = 3;
    g.strokeRect(-tw / 2, -22, tw, 40);
    g.fillStyle = RED;
    g.textAlign = 'center';
    g.fillText(t, 0, 10);
    g.restore();
    sx += tw + 16;
  });
  g.restore();
  // The ticket: race me today.
  g.save();
  g.translate(W - 210, H - 110);
  g.rotate((5 * Math.PI) / 180);
  g.shadowColor = 'rgba(59,42,30,0.35)';
  g.shadowOffsetY = 6;
  g.fillStyle = RED;
  g.fillRect(-150, -48, 300, 96);
  g.shadowColor = 'transparent';
  g.fillStyle = '#f3e6c8';
  g.textAlign = 'center';
  g.font = `700 15px ${FONT_TEXT}`;
  g.fillText((w.champion ? 'BEAT THE CHAMPION' : 'RACE ME TODAY').split('').join(' '), 0, -16);
  g.font = `400 50px ${FONT_DISPLAY}`;
  g.fillText(host, 0, 34);
  g.restore();
  return new Promise((done) => c.toBlob((b) => done(b), 'image/jpeg', 0.88));
}
